import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { ApiError, ApiResponse } from '../types/api/network';
import { handleLogout, isLogoutInProgress, userStore } from '../features/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// A JWT is three non-empty base64url segments separated by dots. Checking
// this before rotating the stored session token guards against a
// misconfigured proxy/CDN or any endpoint accidentally echoing a header
// named "token" that isn't actually a valid credential.
const JWT_SHAPE_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

const isPlausibleJwt = (value: unknown): value is string => typeof value === 'string' && JWT_SHAPE_PATTERN.test(value);

class Network {
  private axiosInstance: AxiosInstance;
  private sessionTokenPromise: Promise<string | null> | null = null;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      withCredentials: true,
    });

    this.initializeInterceptors();
  }

  private initializeInterceptors() {
    this.axiosInstance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const token = await this.getLatestAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => Promise.reject(error)
    );

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        const newToken = response.headers?.token;
        if (isPlausibleJwt(newToken)) {
          userStore.setAuthToken(newToken);
        }
        return response;
      },
      error => {
        if (error?.response?.status === 401 && !isLogoutInProgress()) {
          handleLogout();
        }

        if (error?.response) {
          return Promise.reject({
            status: error.response.status,
            message: error.response.data?.message || error.response.statusText || 'Request failed',
            errors: error.response.data?.errors,
          } as ApiError);
        }

        return Promise.reject({
          status: 0,
          message: error.message || 'Network error',
        } as ApiError);
      }
    );
  }

  private getLatestAccessToken = async (): Promise<string | null> => {
    const fallbackToken = userStore.getAuthToken();

    if (!this.sessionTokenPromise) {
      this.sessionTokenPromise = fetchAuthSession()
        .then(session => session.tokens?.accessToken?.toString() ?? null)
        .catch(() => fallbackToken)
        .finally(() => {
          this.sessionTokenPromise = null;
        });
    }

    const latestToken = await this.sessionTokenPromise;

    if (latestToken && latestToken !== fallbackToken) {
      userStore.setAuthToken(latestToken);
    }

    return latestToken ?? fallbackToken;
  };

  private normalizeResponse<T>(response: AxiosResponse): ApiResponse<T> {
    return {
      data: response.data?.data ?? response.data,
      status: response.status,
      headers: response.headers,
      url: response.config.url,
    };
  }

  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.get<T>(url, config).then(res => this.normalizeResponse<T>(res));
  }

  post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.post<T>(url, data, config).then(res => this.normalizeResponse<T>(res));
  }

  put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.put<T>(url, data, config).then(res => this.normalizeResponse<T>(res));
  }

  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.delete<T>(url, config).then(res => this.normalizeResponse<T>(res));
  }

  patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.patch<T>(url, data, config).then(res => this.normalizeResponse<T>(res));
  }
}

export const network = new Network();
